import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { 
    LAYOUT_HEIGHT, 
    DIVERT_RETURN_STATUS, 
    EXCLUDED_FLIGHT_STATUS 
} from 'state/constants';
import { removeItemByValueImmutable, filterFlights } from 'state/helpers';
import { 
    countFlightsByField,
    countFlightsBy2Fields,
    countStackedFlightsBy2Fields,
    countFlightsByTableau,
} from './utils';
import CommonTableau from './CommonTableau';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import FlightFilterDialog from 'components/FlightFilterDialog';
import ChartDialog from 'components/chartNivo/ChartDialog';
import PieChart from 'components/chartNivo/PieChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import { boxStyles as styles } from 'components/CustomStyles';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const viewOptions = {
    chart: [
        { value: "flightsByFlightDate", label: "by Date" },
        { value: "flightsByDep", label: "by Port" },
        { value: "flightsBySector", label: "by Sector" },
        { value: "flightsBySectorType", label: "by Sector Type" },
        { value: "flightsByFlightType", label: "by Flight Type" },
        { value: "flightsByFlightReg", label: "by Flight Reg" },
        { value: "flightsByACType", label: "by AC Type" },
        { value: "flightsByCountry", label: "by Country" },
        { value: "flightsByFlightNo", label: "by Flight No" },
    ],
    tableau: [
        { value: "byDate", label: "by Date" },
        { value: "byDateDep", label: "by Date & Port" },
        { value: "byDateSector", label: "by Date & Sector" },
        { value: "byDateSectorType", label: "by Date & Sector Type" },
        { value: "byDateFlightType", label: "by Date & Flight Type" },
        { value: "byDateFlightReg", label: "by Date & Flight Reg" },
        { value: "byDateACType", label: "by Date & AC Type" },
        { value: "byDateCountry", label: "by Date & Country" },
        { value: "byDateFlightNo", label: "by Date & Flight No" },

        { value: "byDep", label: "by Port" },
        { value: "bySector", label: "by Sector" },
        { value: "bySectorType", label: "by Sector Type" },
        { value: "byFlightType", label: "by Flight Type" },

        { value: "byFlightReg", label: "by Flight Reg" },
        { value: "byFlightRegDep", label: "by Reg & Port" },
        { value: "byFlightRegSector", label: "by Reg & Sector" },
        { value: "byFlightRegSectorType", label: "by Reg & Sector Type" },
        { value: "byFlightRegFlightType", label: "by Reg & Flight Type" },
        { value: "byFlightRegCountry", label: "by Reg & Country" },

        { value: "byACType", label: "by AC Type" },
        { value: "byACTypeDep", label: "by AC & Port" },
        { value: "byACTypeSector", label: "by AC & Sector" },
        { value: "byACTypeSectorType", label: "by AC & Sector Type" },
        { value: "byACTypeFlightType", label: "by AC & Flight Type" },
        { value: "byACTypeCountry", label: "by AC & Country" },

        { value: "byCountry", label: "by Country" },
        { value: "byFlightNo", label: "by Flight No" },
    ],
};

const FlightChart = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [chartOpen, setChartOpen] = useState(false);    
    const [selectedChart, setSelectedChart] = useState(null);

    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState('flightsByFlightDate');

    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;
        const isValid = options.some(opt => opt.value === view);
        if (!isValid) setView(options[0].value);
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view)
            ? view
            : options[0]?.value || '';
    }, [view, displayType]);
  
    const flightsData = useSelector((state) => state.flights); 
    
    const cacheRef = useRef({});
    const flights = useMemo(() => {
        return removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
            .filter(f => !DIVERT_RETURN_STATUS.includes(f.flightStatus));
    }, [flightsData]);

    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);

    const chartData = useMemo(() => {
        const cacheKey = `${view}-${timeMode}-${filteredFlights.length}`;

        if (cacheRef.current[cacheKey]) {
            return cacheRef.current[cacheKey];
        }
        
        const flightDate = timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";
        const f = filteredFlights || [];
        let result = {};

        switch (view) {
            case "flightsByFlightDate":
                result = {
                    flightsByFlightDate: countFlightsByField(f, flightDate),
                    flightsAndBlockHoursByFlightDate: countFlightsBy2Fields(f, flightDate, 'blockHours'),
                    // flightsByFlightDateDep: countStackedFlightsBy2Fields(f, flightDate, 'dep'), 
                    // flightsByFlightDateSector: countStackedFlightsBy2Fields(f, flightDate, 'sector'), 
                    flightsByFlightDateSectorType: countStackedFlightsBy2Fields(f, flightDate, 'sectorType'),
                    flightsByFlightDateFlightType: countStackedFlightsBy2Fields(f, flightDate, 'flightType'),
                    // flightsByFlightDateFlightReg: countStackedFlightsBy2Fields(f, flightDate, 'flightReg'),
                    flightsByFlightDateACType: countStackedFlightsBy2Fields(f, flightDate, 'acType'),
                    // flightsByFlightDateCountry: countStackedFlightsBy2Fields(f, flightDate, 'depCountryName'),
                    // flightsByFlightDateFlightNo: countStackedFlightsBy2Fields(f, flightDate, 'flightNo'),
                };
                break;
            case "flightsByDep":
                result = {
                    flightsByDep: countFlightsByField(f, "dep", "column"),
                    flightsAndBlockHoursByDep: countFlightsBy2Fields(f, "dep", "blockHours"),
                    flightsByDepSectorType: countStackedFlightsBy2Fields(f, "dep", "sectorType"),
                    flightsByDepFlightType: countStackedFlightsBy2Fields(f, "dep", "flightType"),
                };
                break;
            case "flightsBySector":
                result = {
                    flightsBySector: countFlightsByField(f, "sector", "column"),
                    flightsAndBlockHoursBySector: countFlightsBy2Fields(f, "sector", "blockHours"),
                    flightsBySectorFlightType: countStackedFlightsBy2Fields(f, "sector", "flightType"),
                };
                break;
            case "flightsBySectorType":
                result = {
                    flightsBySectorType: countFlightsByField(f, "sectorType", "pie"),
                    flightsAndBlockHoursBySectorType: countFlightsBy2Fields(f, "sectorType", "blockHours"),
                    flightsBySectorTypeFlightType: countStackedFlightsBy2Fields(f, "sectorType", "flightType"),
                    flightsBySectorTypeACType: countStackedFlightsBy2Fields(f, "sectorType", "acType"),
                    flightsBySectorTypeCountry: countStackedFlightsBy2Fields(f, "sectorType", "depCountryName"),
                };
                break;
            case "flightsByFlightType":
                result = {
                    flightsByFlightType: countFlightsByField(f, "flightType", "pie"),
                    flightsAndBlockHoursByFlightType: countFlightsBy2Fields(f, "flightType", "blockHours"),
                    flightsByFlightTypeSectorType: countStackedFlightsBy2Fields(f, "flightType", "sectorType"),
                };
                break;
            case "flightsByFlightReg":
                result = {
                    flightsByFlight_Reg: countFlightsByField(f, "flightReg", "pie"),
                    flightsByFlightReg: countFlightsByField(f, "flightReg", "column"),
                    flightsAndBlockHoursByFlightReg: countFlightsBy2Fields(f, "flightReg", "blockHours"),
                    flightsByFlightRegSectorType: countStackedFlightsBy2Fields(f, "flightReg", "sectorType"),
                    flightsByFlightRegFlightType: countStackedFlightsBy2Fields(f, "flightReg", "flightType"),
                    flightsByFlightRegCountry: countStackedFlightsBy2Fields(f, "flightReg", "depCountryName"),
                };
                break;
            case "flightsByACType":
                result = {
                    flightsByACType: countFlightsByField(f, "acType", "pie"),
                    flightsAndBlockHoursByACType: countFlightsBy2Fields(f, "acType", "blockHours"),
                    flightsByACTypeSectorType: countStackedFlightsBy2Fields(f, "acType", "sectorType"),
                    flightsByACTypeFlightType: countStackedFlightsBy2Fields(f, "acType", "flightType"),
                    flightsByACTypeCountry: countStackedFlightsBy2Fields(f, "acType", "depCountryName"),
                };
                break;
            case "flightsByCountry":
                result = {
                    flightsByCountry: countFlightsByField(f, "depCountryName", "pie"),
                    flightsAndBlockHoursByCountry: countFlightsBy2Fields(f, "depCountryName", "blockHours"),
                    flightsByCountrySectorType: countStackedFlightsBy2Fields(f, "depCountryName", "sectorType"),
                    flightsByCountryFlightType: countStackedFlightsBy2Fields(f, "depCountryName", "flightType"),
                    flightsByCountryACType: countStackedFlightsBy2Fields(f, "depCountryName", "acType"),
                };
                break;
            case "flightsByFlightNo":
                result = {
                    flightsByFlightNo: countFlightsByField(f, "flightNo", "column"),
                    flightsAndBlockHoursByFlightNo: countFlightsBy2Fields(f, "flightNo", "blockHours"),
                    flightsByFlightNoSectorType: countStackedFlightsBy2Fields(f, "flightNo", "sectorType"),
                    flightsByFlightNoFlightType: countStackedFlightsBy2Fields(f, "flightNo", "flightType"),
                    flightsByFlightNoCountry: countStackedFlightsBy2Fields(f, "flightNo", "depCountryName"),
                };
                break;
            default:
                break;
        };
        
        cacheRef.current[cacheKey] = result;
        return result;
    }, [timeMode, view, filteredFlights]);

    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const flightDate = timeMode === "UTC" ? "flightDate" : "flightDateLocalBase";
        const f = filteredFlights || [];

        const fieldMap = {
            byDate:              [flightDate, null],
            byDateDep:           [flightDate, 'dep'],
            byDateSector:        [flightDate, 'sector'],
            byDateSectorType:    [flightDate, 'sectorType'],
            byDateFlightType:    [flightDate, 'flightType'],
            byDateFlightReg:     [flightDate, 'flightReg'],
            byDateACType:        [flightDate, 'acType'],
            byDateCountry:       [flightDate, 'depCountryName'],
            byDateFlightNo:      [flightDate, 'flightNo'],
            byDep:               ['dep', null],
            bySector:            ['sector', null],
            bySectorType:        ['sectorType', null],
            byFlightType:        ['flightType', null],
            byFlightReg:         ['flightReg', null],
            byFlightRegDep:      ['flightReg', 'dep'],
            byFlightRegSector:   ['flightReg', 'sector'],
            byFlightRegSectorType: ['flightReg', 'sectorType'],
            byFlightRegFlightType: ['flightReg', 'flightType'],
            byFlightRegCountry:  ['flightReg', 'depCountryName'],
            byACType:            ['acType', null],
            byACTypeDep:         ['acType', 'dep'],
            byACTypeSector:      ['acType', 'sector'],
            byACTypeSectorType:  ['acType', 'sectorType'],
            byACTypeFlightType:  ['acType', 'flightType'],
            byACTypeCountry:     ['acType', 'depCountryName'],
            byCountry:           ['depCountryName', null],
            byFlightNo:          ['flightNo', null],
        };

        const fields = fieldMap[safeView];
        if (!fields) return [];
        return countFlightsByTableau(f, fields[0], fields[1]);
    }, [displayType, safeView, timeMode, filteredFlights]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };
         
    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]);
    

    const handleChartClose = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const chartMap = useMemo(() => {
        const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
        const defaultTitle = `Flight ${period}`;

        return (
            {
                flightsByFlightDate: [
                    { component: <SortBarMultiLinesChart data={chartData.flightsByFlightDate} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} barTranslateXOffSet={130} lineTranslateXOffSet={0} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByFlightDate} title={defaultTitle} bottomLegend='Date' leftLegend='Value' digit={0} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },            
                    // { component: <SortStackedBarChart data={chartData.flightsByFlightDateDep} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} itemWidthOffSet={-40} />, style: {} },
                    // { component: <SortStackedBarChart data={chartData.flightsByFlightDateSector} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} itemWidthOffSet={-15} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightDateSectorType} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} translateXOffSet={-10} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightDateFlightType} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} translateXOffSet={-15} itemWidthOffSet={25} />, style: {} },
                    // { component: <SortStackedBarChart data={chartData.flightsByFlightDateFlightReg} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} translateXOffSet={-10} itemWidthOffSet={-20} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightDateACType} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} translateXOffSet={-10} itemWidthOffSet={-35} />, style: {} },
                    // { component: <SortStackedBarChart data={chartData.flightsByFlightDateCountry} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} translateXOffSet={-15} itemWidthOffSet={-15} />, style: {} },
                    // { component: <SortStackedBarChart data={chartData.flightsByFlightDateFlightNo} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={0} itemWidthOffSet={-40} />, style: {} },
                ],
                flightsByDep: [
                    { component: <SortBarChart data={chartData.flightsByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Flights' digit={0} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByDep} title={defaultTitle} bottomLegend='Port' leftLegend='Value' digit={0} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5}/>, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByDepSectorType} title={defaultTitle} bottomLegend='Port' leftLegend='Flights' digit={0} itemWidthOffSet={10} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByDepFlightType} title={defaultTitle} bottomLegend='Port' leftLegend='Flights' digit={0} translateXOffSet={-15} itemWidthOffSet={25} />, style: {} },
                ],
                flightsBySector: [
                    { component: <SortBarChart data={chartData.flightsBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='Flights' digit={0} bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='Value' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsBySectorFlightType} title={defaultTitle} bottomLegend='Sector' leftLegend='Flights' digit={0} bottomOffSet={10} bottomLegendOffSet={10} translateXOffSet={-15} itemWidthOffSet={25} />, style: {} },
                ],
                flightsBySectorType: [
                    { component: <PieChart data={chartData.flightsBySectorType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Flights' digit={0} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursBySectorType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Value' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsBySectorTypeFlightType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Flights' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} translateXOffSet={-15} itemWidthOffSet={25} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsBySectorTypeACType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Flights' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} translateXOffSet={-10} itemWidthOffSet={-35} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsBySectorTypeCountry} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Flights' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} translateXOffSet={-15} itemWidthOffSet={-15} />, style: {} },
                ],
                flightsByFlightType: [
                    { component: <PieChart data={chartData.flightsByFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Flights' isHorizontalBottomLegend={true} digit={0} itemWidthOffSet={20} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Value' isHorizontalBottomLegend={!isMobile ? true : undefined} digit={0} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5}/>, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightTypeSectorType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Flights' isHorizontalBottomLegend={!isMobile ? true : undefined} digit={0} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} itemWidthOffSet={10} />, style: {} },
                ],
                flightsByFlightReg: [
                    ...(chartData.flightsByFlight_Reg?.length < 10 ? [
                        { component: <PieChart data={chartData.flightsByFlight_Reg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} itemsSpacingOffSet={-25} />, style: {} }
                    ] : []),
                    { component: <SortBarChart data={chartData.flightsByFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Value' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5}/>, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightRegSectorType} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} itemWidthOffSet={10} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightRegFlightType} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-15} itemWidthOffSet={25} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightRegCountry} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-15} itemWidthOffSet={-15} />, style: {} },
                ],
                flightsByACType: [
                    { component: <PieChart data={chartData.flightsByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={0} itemWidthOffSet={-40} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Value' digit={0} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },          
                    { component: <SortStackedBarChart data={chartData.flightsByACTypeSectorType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-10} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByACTypeFlightType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-15} itemWidthOffSet={25} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByACTypeCountry} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-15} itemWidthOffSet={-15} />, style: {} },
                ],
                flightsByCountry: [
                    ...(chartData.flightsByCountry?.length < 10 ? [
                        { component: <PieChart data={chartData.flightsByCountry} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={0} itemsSpacingOffSet={-15} />, style: {} }
                    ] : []),
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByCountry} title={defaultTitle} bottomLegend='Country' leftLegend='Value' digit={0} bottomOffSet={45} bottomLegendOffSet={45} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5} />, style: {} },            
                    { component: <SortStackedBarChart data={chartData.flightsByCountrySectorType} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={0} bottomOffSet={45} bottomLegendOffSet={45} translateXOffSet={-10} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByCountryFlightType} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={0} bottomOffSet={45} bottomLegendOffSet={45} translateXOffSet={-15} itemWidthOffSet={25} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByCountryACType} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' digit={0} bottomOffSet={45} bottomLegendOffSet={45} translateXOffSet={-15} itemWidthOffSet={-35} />, style: {} },
                ],
                flightsByFlightNo: [
                    { component: <SortBarChart data={chartData.flightsByFlightNo} title={defaultTitle} bottomLegend='Flight No' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} />, style: {} },
                    { component: <SortBarMultiLinesChart data={chartData.flightsAndBlockHoursByFlightNo} title={defaultTitle} bottomLegend='Flight No' leftLegend='Value' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={5} lineTranslateXOffSet={-5} itemWidthOffSet={-5}/>, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightNoSectorType} title={defaultTitle} bottomLegend='Flight No' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} itemWidthOffSet={10} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightNoFlightType} title={defaultTitle} bottomLegend='Flight No' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-15} itemWidthOffSet={25} />, style: {} },
                    { component: <SortStackedBarChart data={chartData.flightsByFlightNoCountry} title={defaultTitle} bottomLegend='Flight No' leftLegend='Flights' digit={0} bottomOffSet={5} bottomLegendOffSet={5} translateXOffSet={-15} itemWidthOffSet={-15} />, style: {} },
                ],
            }
        )
    }, [startDate, endDate, isMobile, chartData]);
    
    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts?.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="Flight" 
                    subtitle={displayType === 'chart' 
                        ? 'List of Charts' 
                        : 'List of Tableaus'
                    }
                />     

                <Box display="flex" alignItems="center" gap={1}>                    
                    <FormControl 
                        size="small" 
                        sx={{ width: isMobile ? "6rem" : "8rem" }}
                    >
                        <InputLabel>Display</InputLabel>
                        <Select
                            value={displayType}
                            label="Display"
                            onChange={(e) => setDisplayType(e.target.value)}
                        >
                            {displayOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    <FormControl
                        size="small"
                        sx={{ width: isMobile ? '7rem' : '12rem' }}
                    >
                        <InputLabel id="flights-view-label">View</InputLabel>
                        <Select
                            labelId="flights-view-label"
                            label="View"
                            value={safeView}
                            onChange={(e) => setView(e.target.value)}
                        >
                            {viewOptions[displayType].map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FilterButton
                        filters={filters}
                        onClick={handleFilterOpen}
                    />
                </Box>                   
            </FlexBetween>

            <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            >
                {displayType === 'chart' && safeView in chartMap &&
                    renderCharts(chartMap[safeView], { minHeight: LAYOUT_HEIGHT })
                }

                {displayType === 'tableau' &&
                    renderCharts([{ component: <CommonTableau data={tableauData} mode="count" />, style: {} }], { minHeight: LAYOUT_HEIGHT })
                }

                <ScrollToTop scrollRef={scrollRef} />
            </Box>

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleChartClose}
                    chartComponent={selectedChart}
                />
            )}
        </Box>
    );
};

export default FlightChart;
